import * as React from 'react';
import { CommonStyles } from '../common/react/CommonStyles';
import { ButtonType, DialogFooterButtonsWidget, DialogType } from '@tableau/widgets';
import { ModalInstance, ReactModalService } from '../commonInfra/dialogs/ReactModalService';
import { Messages } from '../codegen/Localize';
import { GetPostLoginNotificationsRequest, IGetPostLoginNotificationsResult } from '../ts/ServerApi/requests/GetPostLoginNotificationsRequest';
import * as _ from 'underscore';
import * as queryString from 'query-string';
import WebHttpClient from '../httpClient/WebHttpClient';
import { takeFirstInstanceOf } from '~src/ts/helpers/UrlHelpers';

const postLoginDialogStyle: React.CSSProperties = {
  width: 'auto',
  height: 'auto',
  paddingTop: CommonStyles.Sizes.LargeSpace,
  paddingBottom: CommonStyles.Sizes.MediumSpace,
  paddingLeft: CommonStyles.Sizes.MediumSpace,
  paddingRight: CommonStyles.Sizes.MediumSpace
};

export interface PostLoginDialogProps {
  readonly close: (result: any) => void;
  readonly url: string;
  readonly urlQueryJsonString: string;
}

export class PostLoginNotification {
  static readonly DEFAULT_CACHE_DAYS = 1;
  static readonly ENDING_TIME_KEY = 'endingTime';
  static readonly STARTING_TIME_KEY = 'startingTime';
  static readonly HASHED_CONTENT_KEY = 'hashedContent';

  public notificationsEnabled: boolean;
  public notificationsShown: boolean;
  public checkboxIsClicked: boolean;

  constructor() {
    this.notificationsEnabled = false;
    this.notificationsShown = false;
    this.checkboxIsClicked = false;
  }

  public enableNotifications(): void {
    this.notificationsEnabled = true;
  }

  public notificationEnabled() {
    return this.notificationsEnabled;
  }

  public showNotificationsIfEnabled() {
    if (!this.notificationsEnabled || this.notificationsShown) {
      return;
    }

    this.fetchNotificationsUrl().then((url: string) => {
      if (url) {
        const cachedUrl = this.removeSearchKey(url);
        let shouldDispplay = false;

        const cachedQueryJson = JSON.parse(localStorage.getItem(cachedUrl));
        let curQueryJson = this.getUrlQueryStringJson(url);

        if (cachedQueryJson == null) {
          // There is no cached timestamp, so display the notification
          if (curQueryJson == null) {
            curQueryJson = this.getDefaultUriQueryJson();
          }
          shouldDispplay = true;
        }
        else if (curQueryJson == null) {
          // There is a cached timestamp, but the url query string is missing or mis-formatted.
          // So use TODAY to decide whether to display the notification
          let curTimestamp = new Date();
          let diff = curTimestamp.valueOf() - cachedQueryJson[PostLoginNotification.ENDING_TIME_KEY];
          if (diff >= 0) {
            localStorage.removeItem(cachedUrl);
            curQueryJson = this.getDefaultUriQueryJson();
            shouldDispplay = true;
          }
        }
        else if (!_.isEqual(cachedQueryJson, curQueryJson)) {
          // There is some change in the current query string.
          // So there is a new notification, and remove the old one
          localStorage.removeItem(cachedUrl);
          shouldDispplay = true;
        }

        if (shouldDispplay) {
          let modalInstance: ModalInstance;
          const dialogProps = {
            close: (result: any) => modalInstance && modalInstance.close(result),
            url: cachedUrl,
            urlQueryJsonString: JSON.stringify(curQueryJson)
          };
          modalInstance = ReactModalService.open({
            type: DialogType.Uber,
            dialogContent: React.createElement(PostLoginDialog, dialogProps),
            testId: 'postlogin-test-id',
            showCloseButton: true,
            dialogStyle: postLoginDialogStyle,
            shouldNotSetDefaultWidth: true,
            footerContent:
              <DialogFooterButtonsWidget
                  buttons={[
                      {
                          handleClick: () => {
                            modalInstance.close();
                          },
                          buttonType: ButtonType.Outline,
                          children: Messages.close(),
                          testId: 'postlogin-footer-close'
                      }
                  ]}
                  testId="postlogin-footer"
              />
          });
        }
      }
    }).finally(() => {
      this.notificationsShown = true;
    }); // Suppress in case of future remounting
  }

  public fetchNotificationsUrl(): Promise<string> {
    return WebHttpClient.sendRequest(new GetPostLoginNotificationsRequest())
      .then((result: IGetPostLoginNotificationsResult) => result.contentUrl);
  }

  // Get the url without the search keys
  public removeSearchKey(url: string) {
    let resultUrl = null;
    if (url) {
      const urlObject = queryString.parseUrl(url);
      resultUrl = urlObject.url;
    }
    return resultUrl;
  }

  // parse the query string, and put them into a json object
  public getUrlQueryStringJson(url: string) {
    let json = null;

    const urlObject = queryString.parseUrl(url);

    let value = takeFirstInstanceOf(urlObject.query[PostLoginNotification.STARTING_TIME_KEY]);
    // Convert seconds to milliseconds
    const startTime = this.getValidDateTime(value) * 1000;

    value = takeFirstInstanceOf(urlObject.query[PostLoginNotification.ENDING_TIME_KEY]);
    // Convert seconds to milliseconds
    const endTime = this.getValidDateTime(value) * 1000;

    const content = urlObject.query[PostLoginNotification.HASHED_CONTENT_KEY];

    // If query parameters are available
    if (startTime > 0 && endTime > 0 && startTime < endTime && content != null) {
      const curDate = new Date();
      // If the time range is valid
      if (curDate.valueOf() < endTime) {
        json = {startingTime: startTime, endingTime: endTime, hashedContent: content};
      }
    }
    return json;
  }

  // Return the default json objec that has the 24 hours' time range from now
  private getDefaultUriQueryJson() {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + PostLoginNotification.DEFAULT_CACHE_DAYS);

    const json = { startingTime: today.getTime(), endingTime: tomorrow.getTime(), hashedContent: '' };
    return json;
  }

  // Parse the date time, and return O for an invalid value
  private getValidDateTime(timeString: string) {
    var num = 0;
    if (timeString != null) {
      num = parseInt(timeString) || 0;
    }
    return num;
  }
}

export class PostLoginDialog extends React.Component<PostLoginDialogProps> {
  private checkboxIsChecked: boolean;
  private url: string;
  private urlQueryJsonString: string;

  constructor(props: PostLoginDialogProps) {
    super(props);
    this.url = props.url;
    this.urlQueryJsonString = props.urlQueryJsonString;
    this.checkboxIsChecked = false;
  }

  public onChange = () => {
    if (this.checkboxIsChecked) {
      let value = localStorage.getItem(this.url);
      if (value != null) {
        localStorage.removeItem(this.url);
      }
    } else {
      localStorage.setItem(this.url, this.urlQueryJsonString);
    }
    this.checkboxIsChecked = !this.checkboxIsChecked;
  }

  public render() {
    return(
      <div>
          <iframe id='maintenance-notification' title='maintenance notification' src={this.url} width='800px' height='300px'>
          </iframe>
          <div style={{paddingTop: '8px'}}>
            <input type="checkbox" onChange={this.onChange} defaultChecked={this.checkboxIsChecked}/>
            <label>
              {Messages.doNotShowNotificationAgain()}
            </label>
        </div>
      </div>
    );
  }
}

export const postLoginNotification = new PostLoginNotification();
